import angular from 'angular';
import bootstrap from 'angular-ui-bootstrap';

import i18n from '../i18n';
import menuButtonItem from './menu-button-item-directive';
import menuButtonTrigger from './menu-button-trigger-directive';
import menuButtonDivider from './menu-button-divider-directive';
import menuButton from './menu-button-directive';
import submenu from './submenu-directive.js';

/**
 * @ngdoc module
 * @name akamai.components.menu-button
 *
 * @guideline https://design.akamai.com/components/menu/
 *
 * @description
 * Menu buttons display a list of clickable actions.
 *
 * @example menu-button.html
 * <akam-menu-button>
 *   <akam-menu-button-item defaultText="an.i18n.key" ng-click="..."></akam-menu-button-item>
 *   <akam-menu-button-item text="..." ng-click="..."></akam-menu-button-item>
 *   <akam-menu-button-item text="..." ng-click="..."></akam-menu-button-item>
 * </akam-menu-button>
 *
 *  <!-- and for a split button -->
 *  <akam-menu-button default-text="examples.appNames.tq" on-click="vm.process('tq')">
 *    <!-- same as above -->
 *    <akam-menu-button-item></akam-menu-button-item>
 *  </akam-menu-button>
 */
export default angular.module('akamai.components.menu-button', [
  bootstrap,
  i18n.name
])

/**
 * @ngdoc directive
 * @name akamMenuButton
 * @description Creates a menu button control.
 * @restrict E
 *
 * @param {string} [position="left"] Where the dropdown menu should be positioned. Should be either
 * "left" or "right".
 *
 * @param {string} [size="medium"] The size of the button. Can be "small", "medium" or "large".
 *
 * @param {string} [hide-caret] The presence of this attribute will override the appearance of the
 * caret/arrow that appears above the dropdown.
 *
 * @param {String|TranslateKey} [text=""] The text to show. If present, will render as a button
 * with the text and a caret icon.
 *
 * @param {string|TranslateKey} [default-text] The presence of this attribute will cause the menu
 * button to be rendered as a split button. Note that this means that the default item will not be
 * rendered in the dropdown.
 *
 * @param {Object} [default-text-values] If `TranslateKey` is used for `default-text`, this object
 * hash will be used for translations with variable interpolation. See
 * [angular-translate's translate-value support](https://angular-translate.github.io/docs/#/guide/06_variable-replacement).
 *
 * @param {function()} [on-click] Callback for when the button is clicked.
 * @param {*} [is-disabled] Method or property that determines if this item should be disabled.
 */
  .directive('akamMenuButton', menuButton)

/**
 * @ngdoc directive
 * @name akamMenuButtonItem
 * @restrict E
 *
 * @param {String|TranslateKey} [text=""] The text to show.
 *
 * @param {Object} [text-values] If `TranslateKey` is used for `text`, this object
 * hash will be used for translations with variable interpolation. See
 * [angular-translate's translate-value support](https://angular-translate.github.io/docs/#/guide/06_variable-replacement).
 *
 * @param {boolean} [is-disabled=false] If disabled, no user interaction will be possible.
 *
 * @param {string} [icon] An optional aci icon class.
 *
 * @example see {@link akamai.components.menu-button.directive:akamMenuButton}
 * @description Creates a menu button item within the menu button control.
 **/
  .directive('akamMenuButtonItem', menuButtonItem)

  /**
   * @ngdoc directive
   * @name akamMenuButtonTrigger
   * @restrict E
   *
   * @example see {@link akamai.components.menu-button.directive:akamMenuButton}
   * @description Creates a custom menu button trigger within the menu button control.
   * The dropdown menu will be anchored to this element.
   **/
  .directive('akamMenuButtonTrigger', menuButtonTrigger)

  /**
   * @ngdoc directive
   * @name akamMenuButtonDivider
   * @restrict E
   *
   * @example see {@link akamai.components.menu-button.directive:akamMenuButton}
   * @description Adds a divider in the dropdown between dropdown menu items.
   **/
  .directive('akamMenuButtonDivider', menuButtonDivider)

  /**
   * @name akamSubmenu
   * @restrict E
   *
   * @example see {@link akamai.components.menu-button.directive:akamMenuButton}
   * @description Creates an item that launches a submenu within the menu button control.
   **/
  .directive('akamSubmenu', submenu);
